import React, { useState, useEffect } from 'react';
import { Wallet, ArrowDownToLine, ArrowUpFromLine, Clock, CheckCircle, XCircle, CreditCard, Landmark, Loader2 } from 'lucide-react';
import { walletAPI } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

const TX_ICONS = {
  credit: <ArrowDownToLine className="w-4 h-4 text-emerald-500" />,
  purchase: <ArrowUpFromLine className="w-4 h-4 text-[#0C63BF]" />,
  withdrawal: <Landmark className="w-4 h-4 text-amber-500" />,
  refund: <ArrowDownToLine className="w-4 h-4 text-purple-500" />,
};

const TX_STATUS_BADGE = {
  completed: { label: 'wal.tx_completed', cls: 'bg-emerald-100 text-emerald-700' },
  pending: { label: 'wal.tx_pending', cls: 'bg-amber-100 text-amber-700' },
  rejected: { label: 'wal.tx_rejected', cls: 'bg-red-100 text-red-700' },
};

export default function MyWalletTab() {
  const { t, i18n } = useTranslation();
  const loc = i18n.language === 'en' ? 'en-GB' : `${i18n.language}-${i18n.language.toUpperCase()}`;
  const [wallet, setWallet] = useState(null);
  const [bank, setBank] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showBankForm, setShowBankForm] = useState(false);
  const [bankForm, setBankForm] = useState({ account_holder: '', iban: '', bic: '' });
  const [savingBank, setSavingBank] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [withdrawing, setWithdrawing] = useState(false);

  const loadData = async () => {
    try {
      const [w, b] = await Promise.all([walletAPI.get(), walletAPI.getBankDetails()]);
      setWallet(w.data);
      setBank(b.data);
      if (b.data.has_bank_details) {
        setBankForm({ account_holder: b.data.account_holder, iban: '', bic: b.data.bic });
      }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadData(); }, []);

  const handleSaveBank = async () => {
    if (!bankForm.account_holder.trim() || !bankForm.iban.trim()) {
      toast.error(t('wal.fill_bank'));
      return;
    }
    setSavingBank(true);
    try {
      await walletAPI.updateBankDetails(bankForm);
      toast.success(t('wal.bank_saved'));
      setShowBankForm(false);
      await loadData();
    } catch (err) { toast.error(err.response?.data?.detail || t('wal.error')); }
    finally { setSavingBank(false); }
  };

  const handleWithdraw = async () => {
    const amount = parseFloat(withdrawAmount);
    if (!amount || amount <= 0) { toast.error(t('wal.invalid_amount')); return; }
    if (amount > wallet?.balance) { toast.error(t('wal.insufficient')); return; }
    setWithdrawing(true);
    try {
      await walletAPI.withdraw(amount);
      toast.success(t('wal.withdraw_sent'));
      setWithdrawAmount('');
      await loadData();
    } catch (err) { toast.error(err.response?.data?.detail || t('wal.error')); }
    finally { setWithdrawing(false); }
  };

  if (loading) return <div className="space-y-4">{[...Array(3)].map((_, i) => <div key={i} className="h-24 bg-[#E8F4FD] rounded-2xl animate-pulse" />)}</div>;

  const balance = wallet?.balance || 0;
  const pendingAmount = wallet?.pending_amount || 0;
  const transactions = wallet?.transactions || [];
  const pendingOrders = wallet?.pending_orders || [];

  return (
    <div className="space-y-5 max-w-2xl">
      {/* Balance card */}
      <div className="bg-gradient-to-br from-[#033871] to-[#0C63BF] rounded-2xl p-5 sm:p-6 text-white" data-testid="wallet-balance-card">
        <div className="flex items-center gap-2 mb-3">
          <Wallet className="w-5 h-5 text-white/70" />
          <span className="text-sm font-medium text-white/70">{t('wal.available_balance')}</span>
        </div>
        <p className="text-3xl sm:text-4xl font-black" data-testid="wallet-balance">{balance.toFixed(2)}&euro;</p>
        {pendingAmount > 0 && (
          <div className="flex items-center gap-2 mt-3 bg-white/10 rounded-xl px-3 py-2" data-testid="wallet-pending">
            <Clock className="w-4 h-4 text-amber-300" />
            <span className="text-sm text-white/80">{t('wal.pending_confirm', { amount: pendingAmount.toFixed(2) })}</span>
          </div>
        )}
        <div className="flex gap-2 mt-4">
          <Button
            onClick={() => {
              if (!bank?.has_bank_details) { setShowBankForm(true); toast.error(t('wal.add_bank_first')); return; }
              if (balance <= 0) { toast.error(t('wal.insufficient')); return; }
              setWithdrawAmount(String(balance));
            }}
            className="bg-white text-[#033871] hover:bg-white/90 font-bold gap-1.5"
            disabled={balance <= 0}
            data-testid="withdraw-btn"
          >
            <Landmark className="w-4 h-4" /> {t('wal.withdraw_btn')}
          </Button>
        </div>
      </div>

      {/* Withdrawal form */}
      {withdrawAmount && (
        <div className="bg-white rounded-2xl border border-[#D6E8F0] p-5" data-testid="withdraw-form">
          <h3 className="font-bold text-[#033871] mb-3">{t('wal.withdraw_title')}</h3>
          <p className="text-xs text-[#5A8BA8] mb-3">
            {t('wal.withdraw_hint', { iban: bank?.iban_masked })}
          </p>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Input
                type="number"
                value={withdrawAmount}
                onChange={e => setWithdrawAmount(e.target.value)}
                max={balance}
                step="0.01"
                className="pr-8"
                data-testid="withdraw-amount-input"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-[#5A8BA8]">&euro;</span>
            </div>
            <Button onClick={handleWithdraw} disabled={withdrawing} className="bg-[#32CD32] hover:bg-[#28a428] gap-1.5" data-testid="confirm-withdraw-btn">
              {withdrawing ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowUpFromLine className="w-4 h-4" />}
              {t('wal.confirm')}
            </Button>
            <Button variant="ghost" onClick={() => setWithdrawAmount('')}>{t('wal.cancel')}</Button>
          </div>
        </div>
      )}

      {/* Pending orders (en attente de confirmation) */}
      {pendingOrders.length > 0 && (
        <div className="bg-white rounded-2xl border border-amber-200 p-5" data-testid="pending-orders-section">
          <h3 className="font-bold text-[#033871] mb-1 flex items-center gap-2">
            <Clock className="w-4 h-4 text-amber-500" /> {t('wal.pending_payments')}
          </h3>
          <p className="text-xs text-[#5A8BA8] mb-3">
            {t('wal.pending_hint')}
          </p>
          <div className="space-y-2">
            {pendingOrders.map(o => (
              <div key={o.id} className="flex items-center justify-between p-2.5 rounded-xl bg-amber-50">
                <span className="text-sm text-[#033871]">{o.brand} {o.model}</span>
                <div className="flex items-center gap-2">
                  <span className="font-bold text-amber-600">{(o.seller_payout || o.item_price || 0).toFixed(2)}&euro;</span>
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700">
                    {o.status === 'shipped' ? t('wal.shipped') : t('wal.paid')}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Bank details */}
      <div className="bg-white rounded-2xl border border-[#D6E8F0] p-5" data-testid="bank-details-section">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-bold text-[#033871] flex items-center gap-2">
            <CreditCard className="w-4 h-4" /> {t('wal.bank_details')}
          </h3>
          <Button variant="ghost" size="sm" onClick={() => setShowBankForm(!showBankForm)} className="text-xs text-[#0C63BF]" data-testid="edit-bank-btn">
            {bank?.has_bank_details ? t('wal.edit') : t('wal.add')}
          </Button>
        </div>
        {bank?.has_bank_details && !showBankForm ? (
          <div className="space-y-2" data-testid="bank-details-display">
            <div className="flex justify-between text-sm">
              <span className="text-[#5A8BA8]">{t('wal.holder')}</span>
              <span className="font-medium text-[#033871]">{bank.account_holder}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-[#5A8BA8]">IBAN</span>
              <span className="font-mono text-[#033871]">{bank.iban_masked}</span>
            </div>
            {bank.bic && (
              <div className="flex justify-between text-sm">
                <span className="text-[#5A8BA8]">BIC</span>
                <span className="font-mono text-[#033871]">{bank.bic}</span>
              </div>
            )}
          </div>
        ) : !showBankForm ? (
          <p className="text-sm text-amber-600 bg-amber-50 rounded-xl p-3" data-testid="no-bank-warning">
            {t('wal.no_bank_warning')}
          </p>
        ) : null}
        {showBankForm && (
          <div className="space-y-3 mt-2" data-testid="bank-form">
            <div>
              <Label>{t('wal.holder_label')}</Label>
              <Input
                value={bankForm.account_holder}
                onChange={e => setBankForm(p => ({ ...p, account_holder: e.target.value }))}
                placeholder="Jean Dupont"
                className="mt-1"
                data-testid="bank-holder-input"
              />
            </div>
            <div>
              <Label>IBAN</Label>
              <Input
                value={bankForm.iban}
                onChange={e => setBankForm(p => ({ ...p, iban: e.target.value }))}
                placeholder="FR76 3000 6000 0112 3456 7890 189"
                className="mt-1 font-mono"
                data-testid="bank-iban-input"
              />
            </div>
            <div>
              <Label>BIC / SWIFT <span className="text-[#B8D4E3] font-normal">{t('wal.optional')}</span></Label>
              <Input
                value={bankForm.bic}
                onChange={e => setBankForm(p => ({ ...p, bic: e.target.value }))}
                placeholder="BNPAFRPP"
                className="mt-1 font-mono"
                data-testid="bank-bic-input"
              />
            </div>
            <div className="flex gap-2">
              <Button onClick={handleSaveBank} disabled={savingBank} className="bg-[#32CD32] hover:bg-[#28a428] gap-1.5" data-testid="save-bank-btn">
                {savingBank ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                {t('wal.save')}
              </Button>
              <Button variant="ghost" onClick={() => setShowBankForm(false)}>{t('wal.cancel')}</Button>
            </div>
          </div>
        )}
      </div>

      {/* Transaction history */}
      <div className="bg-white rounded-2xl border border-[#D6E8F0] p-5" data-testid="transactions-section">
        <h3 className="font-bold text-[#033871] mb-3">{t('wal.history')}</h3>
        {transactions.length === 0 ? (
          <p className="text-sm text-[#5A8BA8] text-center py-6">{t('wal.no_tx')}</p>
        ) : (
          <div className="space-y-2">
            {transactions.map(tx => {
              const badge = TX_STATUS_BADGE[tx.status] || TX_STATUS_BADGE.completed;
              return (
                <div key={tx.id} className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-[#F0F7FA] transition-colors" data-testid={`tx-${tx.id}`}>
                  <div className="w-8 h-8 rounded-full bg-[#F0F7FA] flex items-center justify-center shrink-0">
                    {TX_ICONS[tx.type] || TX_ICONS.credit}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-[#033871] font-medium truncate">{tx.description}</p>
                    <p className="text-[10px] text-[#B8D4E3]">{new Date(tx.created_at).toLocaleDateString(loc, { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className={`font-bold text-sm ${tx.amount >= 0 ? 'text-emerald-600' : 'text-[#033871]'}`}>
                      {tx.amount >= 0 ? '+' : ''}{tx.amount.toFixed(2)}&euro;
                    </p>
                    <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-medium ${badge.cls}`}>{t(badge.label)}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
